import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { setMobileSidebarOpen } from '@/store/slices/ui-state.slice'
import { selectIsMobileSidebarOpen } from '@/store/selectors/ui-state.selectors'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import { DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { SideNavDashboard, SideNavMatter } from '@/components/side-nav/SideNav'
import { useSideNavConfig } from './hooks/useSideNavConfig'
import SidebarAccountMenu from './SidebarAccountMenu'
import SidebarItemSupport from './SidebarItemSupport'
import SidebarItemAnonSignIn from './SidebarItemAnonSignIn'
import SidebarSyncFilesCard from './SidebarSyncFilesCard'

/**
 * Mobile Sidebar
 *
 * Mobile version of the main navigation component to display feature navigation items.
 * - Mobile-only slide-out version of the sidebar
 * - Triggered via the hamburger button in the Mobile Top Bar
 */
export default function MobileSidebar() {
  const dispatch = useAppDispatch()
  const { width } = useViewportSizeInPixels()

  const isMobileSidebarOpen = useAppSelector(selectIsMobileSidebarOpen)
  const config = useSideNavConfig()

  // Close the sidebar on screen resize to desktop
  useEffect(() => {
    if (width >= DEVICE_TYPE_BREAKPOINTS.DESKTOP && isMobileSidebarOpen) {
      dispatch(setMobileSidebarOpen(false))
    }
  }, [width, isMobileSidebarOpen, dispatch])

  const handleNavigate = () => {
    dispatch(setMobileSidebarOpen(false))
  }

  const renderFooter = () => (
    <>
      <SidebarItemSupport />
      <SidebarItemAnonSignIn />
      <SidebarSyncFilesCard />
      <SidebarAccountMenu />
    </>
  )

  return (
    <>
      <Transition.Root show={isMobileSidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => dispatch(setMobileSidebarOpen(false))}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-brand-neutral-950/5 backdrop-blur-[20px]" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-2xs flex-1">
                {config.variant === 'dashboard' ? (
                  <SideNavDashboard
                    logoUrl={kDefaultLogoPath}
                    hasFixedLogoHeight={true}
                    activeItemId={config.activeItemId}
                    items={config.items}
                    onNavigate={handleNavigate}
                    onLogoClick={handleNavigate}
                    renderFooter={renderFooter}
                    isSlideover
                  />
                ) : (
                  <SideNavMatter
                    logoUrl={kDefaultLogoPath}
                    hasFixedLogoHeight={true}
                    activeItemId={config.activeItemId}
                    topItems={config.topItems}
                    matterRecords={config.matterRecords}
                    chatHistory={config.chatHistory}
                    onNavigate={handleNavigate}
                    onLogoClick={handleNavigate}
                    renderFooter={renderFooter}
                    isSlideover
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
